import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Confirm, Icons, Modal } from '@components/common';
import PageTitle from '@components/common/PageTitle';
import Table from '@components/common/Table';
import AssetForm from '@components/ModalComponents/AssetForm';
import ExportAssetsRulesForm from '@components/ModalComponents/ExportAssetsRulesForm';
import FileTypeSelectionForm from '@components/ModalComponents/FileTypeSelectionForm';
import ImportMapDataContainer from '@components/ModalComponents/ImportMapDataContainer';

import { useMutateCreateAsset } from '@hooks/assets/useMutateCreateAsset';
import { useMutateDeleteAsset } from '@hooks/assets/useMutateDeleteAsset';
import { useMutateDeleteAssetsById } from '@hooks/assets/useMutateDeleteAssetsById';
import { useMutateExportAssetsFile } from '@hooks/assets/useMutateExportAssetsFile';
import { useMutateUpdateExportAssetsRules } from '@hooks/assets/useMutateUpdateExportAssetsRules';
import { useMutationsAssetVisibilityHide } from '@hooks/assets/useMutationsAssetVisibilityHide';
import { useMutationsAssetVisibilityShow } from '@hooks/assets/useMutationsAssetVisibilityShow';
import { useQueryAssetsList } from '@hooks/assets/useQueryAssetsList';
import { useQueryAssetSubTypesByType } from '@hooks/assets/useQueryAssetSubTypesByType';
import { useQueryAssetTypes } from '@hooks/assets/useQueryAssetTypes';
import { useQueryContactPersons } from '@hooks/assets/useQueryContactPersons';
import { useQueryExportAssetsRules } from '@hooks/assets/useQueryExportAssetsRules';

import { assets as assetsSelector, auth } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	setAssetSiteAvailabilityStatus,
	setAssetSubType,
	setAssetType,
	setBackhaulAccessibilityStatus,
	setContactPersonId,
	setCreatedAt,
	setIsAvailable,
	setLastVerificationDate,
	setPage,
	setPageSize,
	setPowerSupplyAvailabilityStatus,
	setRentStatus,
	setSearchValue,
	setSort,
	setUpdatedAt,
} from '@slices/assets';

import { setFilterUtil } from '@utils/filterUtil';
import { checkGroupPanelAccess } from '@utils/permissions';

import { TAssetVisibilityDTO } from '@interfaces/assets/dto/assetsDTO';
import { IGroupOperationWithActionDTO } from '@interfaces/common/defaultDTO';
import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITableDatepickerFilter,
	ITablePagination,
	ITableSearch,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';

import { EAdminPanelPageTitles, TMenuActionsTypes } from '@enums/common';
import { EImportingDataType } from '@enums/dropzone';

import { exportAssetsRadioButtonsData } from '@constants/common';
import { mapGroupAccess } from '@constants/menuGroupAccess';
import { MODAL_WIDTH_455, MODAL_WIDTH_480, MODAL_WIDTH_580, modalCloseConfirm } from '@constants/modals';
import {
	addTooltipText,
	importTooltipText,
	noSubTypeOptions,
	searchTooltipText,
} from './constants';
import {
	acceptedAssetFileTypes,
} from '@components/ModalComponents/ImportMapDataContainer/ImportMapData/UploadFile/constants';

import CommonValuesMapping from './CommonValuesMapping/CommonValuesMapping';
import { getColumns, getConfirmModalQuestionText, getFilters } from './utils';

import styles from './AssetsList.module.scss';

export interface IAssetVisibilityData {
	dto: TAssetVisibilityDTO;
	isHide: boolean;
}

const AssetsList = () => {
	const { accessPermission } = useAppSelector(auth);

	const {
		assetOwnerOrganizationAdministrator,
		assetOwnerAttributeUpdater,
	} = accessPermission;

	const isMapAvailable = checkGroupPanelAccess(accessPermission, mapGroupAccess);

	const isEditAccess = assetOwnerOrganizationAdministrator || assetOwnerAttributeUpdater;

	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const [isAssetFormModal, setIsAssetFormModal] = useState(false);
	const [isDirtyFormModal, setIsDirtyFormModal] = useState(false);
	const [isCloseConfirmModal, setIsCloseConfirmModal] = useState(false);
	const [singleIdForRemove, setSingleIdForRemove] = useState<string | null>(null);
	const [isShowDeleteConfirm, setIsShowDeleteConfirm] = useState(false);
	const [isShowImportAsset, setIsImportAsset] = useState(false);
	const [isShowExportAssetRulesForm, setIsExportAssetRulesForm] = useState(false);
	const [isOpenFileTypeSelectionModal, setIsOpenFileTypeSelectionModal] = useState(false);
	const [isOpenSetCommonValues, setIsOpenSetCommonValues] = useState(false);
	const [isDirtyCommonValues, setIsDirtyCommonValues] = useState(false);
	const [isShowAssetVisibilityConfirm, setIsShowAssetVisibilityConfirm] = useState(false);

	const [assetVisibilityData, setAssetVisibilityData] = useState<IAssetVisibilityData | null>(null);
	const [checkedIds, setCheckedIds] = useState<Array<string>>([]);
	const [actionId, setActionId] = useState<TMenuActionsTypes>();
	const [selectedType, setSelectedType] = useState<TSelectOption | null>(null);
	const [selectedSubType, setSelectedSubType] = useState<TSelectOption | null>(null);
	const [selectedContactPersonId, setSelectedContactPersonId] = useState<TSelectOption | null>(null);
	const [selectedAccessAvailability, setSelectedAccessAvailability] = useState<TSelectOption | null>(null);
	const [selectedBackhaulAccessibilityStatus, setSelectedBackhaulAccessibilityStatus]
		= useState<TSelectOption | null>(null);
	const [selectedAssetSiteAvailabilityStatus, setSelectedAssetSiteAvailabilityStatus]
		= useState<TSelectOption | null>(null);
	const [selectedPowerSupplyAvailabilityStatus, setSelectedPowerSupplyAvailabilityStatus]
		= useState<TSelectOption | null>(null);
	const [selectedAssetsIds, setSelectedAssetsIds] = useState<string[] | [] | undefined>();
	const [activeGroupTableAction, setActiveGroupTableAction] = useState<TMenuActionsTypes | undefined>();
	const [selectedIsRentedBy, setSelectedIsRentedBy] = useState<TSelectOption | null>(null);

	const toggleAssetModal = () => setIsAssetFormModal(prevState => !prevState);
	const toggleFileTypeSelectionModal = () => setIsOpenFileTypeSelectionModal(prevState => !prevState);
	const toggleAssetVisibilityModal = () => setIsShowAssetVisibilityConfirm(prevState => !prevState);

	const toggleSetCommonValues = () => setIsOpenSetCommonValues(prevState => !prevState);

	const closeModals = () => {
		setIsAssetFormModal(false);
		setIsCloseConfirmModal(false);
		setIsExportAssetRulesForm(false);
		setIsOpenFileTypeSelectionModal(false);
		setIsOpenSetCommonValues(false);
	};

	const { exportAssetsRules } = useQueryExportAssetsRules();
	const { handlePreparingExport, isLoadingExportAssetsFile } = useMutateExportAssetsFile();
	const {
		handleUpdateExportAssetsRules,
		isLoadingUpdateExportAssetRules,
	} = useMutateUpdateExportAssetsRules([closeModals]);

	const toggleCloseConfirmModal = () => {
		if (isDirtyFormModal) setIsCloseConfirmModal(prevState => !prevState);
		else closeModals();
	};

	const onChangeCreatedDate = (value: number | null) => dispatch(setCreatedAt({ createdAt: value }));

	const onChangeUpdatedDate = (value: number | null) => dispatch(setUpdatedAt({ updatedAt: value }));

	const onChangeVerificationDate = (value: number | null) => {
		dispatch(setLastVerificationDate({ lastVerificationDate: value }));
	};

	const openDeleteConfirm = () => setIsShowDeleteConfirm(prevState => !prevState);

	const handleCustomDelete = (data: IGroupOperationWithActionDTO) => {
		openDeleteConfirm();
		setCheckedIds(data.ids);
		setActionId(data.actionType);
	};

	const toggleImportAsset = () => setIsImportAsset(prevState => !prevState);
	const toggleExportAssetRulesForm = () => setIsExportAssetRulesForm(prevState => !prevState);

	const closeDeleteConfirm = () => {
		setIsShowDeleteConfirm(false);
		setSingleIdForRemove(null);
	};

	const closeSetCommonValues = () => setIsOpenSetCommonValues(false);

	const {
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
		assetTypeId,
		assetSubTypeId,
		contactPersonId,
		isAvailable,
		backhaulAccessibilityStatus,
		assetSiteAvailabilityStatus,
		powerSupplyAvailabilityStatus,
		createdAt,
		updatedAt,
		lastVerificationDate,
		rentStatus,
	} = useAppSelector(assetsSelector);

	const { handleDeleteAsset, isDeletingAsset } = useMutateDeleteAsset([closeDeleteConfirm, closeModals]);
	const { handleDeleteAssets, isDeletingAssets } = useMutateDeleteAssetsById({
		filters: {
			assetSiteAvailabilityStatus,
			assetSubTypeId,
			assetTypeId,
			backhaulAccessibilityStatus,
			contactPersonId,
			createdAt,
			isAvailable,
			lastVerificationDate,
			page,
			pageSize,
			powerSupplyAvailabilityStatus,
			rentStatus,
			searchValue,
			sortDirection,
			sortFieldName,
			updatedAt,
		},
		onSettledCallbacks: [closeDeleteConfirm, closeModals],
	});
	const { handleCreateAsset, isCreatingAsset } = useMutateCreateAsset([closeModals]);
	const { handleHideAssets } = useMutationsAssetVisibilityHide();
	const { handleShowAssets } = useMutationsAssetVisibilityShow();

	const handleVisibilityAction = useCallback((assetVisibilityData: IAssetVisibilityData) => {
		setAssetVisibilityData(assetVisibilityData);
		toggleAssetVisibilityModal();
	}, []);

	const handleChangeAssetsVisibility = () => {
		if (!assetVisibilityData) return;

		const { dto, isHide } = assetVisibilityData;

		if (!isHide) {
			handleShowAssets(dto);
		} else {
			handleHideAssets(dto);
		}

		toggleAssetVisibilityModal();
	};

	const {
		assetsList,
		countOfRecords,
		isLoadingAssets,
	} = useQueryAssetsList({
		assetSiteAvailabilityStatus,
		assetSubTypeId,
		assetTypeId,
		backhaulAccessibilityStatus,
		closeDeleteConfirm,
		closeModals,
		contactPersonId,
		createdAt,
		isAdminPanel: true,
		isAvailable,
		lastVerificationDate,
		page,
		pageSize,
		powerSupplyAvailabilityStatus,
		rentStatus,
		searchValue,
		sortDirection,
		sortFieldName,
		updatedAt,
	});

	const { assetTypesSubtypes } = useQueryAssetTypes({ isAssetTypesSubtypesWithIconUrl: false });
	const { assetSubTypesByType } = useQueryAssetSubTypesByType(assetTypeId);
	const { contactPersonsOptions } = useQueryContactPersons();

	const assetSubTypesForFilter = useMemo(() => {
		return assetSubTypesByType?.length ? [...assetSubTypesByType, noSubTypeOptions] : [];
	}, [assetSubTypesByType]);

	const columns = useMemo(() => {
		return getColumns({
			openDeleteConfirm,
			setSingleIdForRemove,
			handleChangeAssetStatus: handleVisibilityAction,
			navigate,
			isEditAccess,
			isEditShowOnMapAccess: isEditAccess,
			isMapAvailable,
		});
	}, [
		handleVisibilityAction,
		isEditAccess,
		navigate,
		isMapAvailable,
	]);

	const deleteItemsHandler = () => {
		if (singleIdForRemove) return handleDeleteAsset(singleIdForRemove);

		return handleDeleteAssets({ ids: checkedIds, actionType: actionId });
	};

	const createdDate = createdAt?.from ? new Date(createdAt.from) : null;
	const updatedDate = updatedAt?.from ? new Date(updatedAt.from) : null;
	const verificationDate = lastVerificationDate?.from ? new Date(lastVerificationDate.from) : null;

	const pageHandler = useCallback(
		(page: number) => dispatch(setPage({ page })),
		[dispatch],
	);

	const pageSizeHandler = useCallback(
		(pageSize: number) => dispatch(setPageSize({ pageSize })),
		[dispatch]);

	const pagination: ITablePagination = useMemo(() => {
		return {
			countOfRecords,
			forcePage: page,
			size: pageSize,
			pageHandler,
			pageSizeHandler,
		};
	}, [
		countOfRecords,
		page,
		pageHandler,
		pageSize,
		pageSizeHandler,
	]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		dispatch(setSort({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		}));
	}, [dispatch]);

	const sort: ITableSort = useMemo(() => {
		return {
			initialState: [{ id: sortFieldName, desc: sortDirection }],
			handleSort,
		};
	}, [
		handleSort,
		sortDirection,
		sortFieldName,
	]);

	const onSearchHandler = useCallback((searchValue: string) => {
		dispatch(setSearchValue({ searchValue }));
	}, [dispatch]);

	const search: Array<ITableSearch> = useMemo(() => {
		return [
			{
				initialValue: searchValue,
				name: 'searchValue',
				handler: onSearchHandler,
				tooltipText: searchTooltipText,
			},
		];
	}, [
		onSearchHandler,
		searchValue,
	]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedType,
			currentValue: assetTypeId,
			setAction: setAssetType,
			fieldName: 'assetType',
			dispatch,
		});

		if (!selectedType || selectedSubType) {
			dispatch(setAssetSubType({ assetSubType: null }));

			setSelectedSubType(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedType]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedSubType,
			currentValue: assetSubTypeId,
			setAction: setAssetSubType,
			fieldName: 'assetSubType',
			dispatch,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedSubType]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedContactPersonId,
			currentValue: contactPersonId,
			setAction: setContactPersonId,
			fieldName: 'contactPersonId',
			dispatch,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedContactPersonId]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedAccessAvailability,
			currentValue: isAvailable,
			setAction: setIsAvailable,
			fieldName: 'isAvailable',
			dispatch,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedAccessAvailability]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedBackhaulAccessibilityStatus,
			currentValue: backhaulAccessibilityStatus,
			setAction: setBackhaulAccessibilityStatus,
			fieldName: 'backhaulAccessibilityStatus',
			dispatch,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedBackhaulAccessibilityStatus]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedAssetSiteAvailabilityStatus,
			currentValue: assetSiteAvailabilityStatus,
			setAction: setAssetSiteAvailabilityStatus,
			fieldName: 'assetSiteAvailabilityStatus',
			dispatch,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedAssetSiteAvailabilityStatus]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedPowerSupplyAvailabilityStatus,
			currentValue: powerSupplyAvailabilityStatus,
			setAction: setPowerSupplyAvailabilityStatus,
			fieldName: 'powerSupplyAvailabilityStatus',
			dispatch,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedPowerSupplyAvailabilityStatus]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedIsRentedBy,
			currentValue: rentStatus,
			setAction: setRentStatus,
			fieldName: 'rentStatus',
			dispatch,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedIsRentedBy]);

	const filters: Array<TTableFilter> = useMemo(() => {
		return getFilters({
			selectedType,
			selectedSubType,
			assetTypes: assetTypesSubtypes,
			assetSubTypesByType: assetSubTypesForFilter,
			selectedContactPersonId,
			contactPersonsOptions,
			contactPersonId,
			selectedAccessAvailability,
			isAvailable,
			selectedBackhaulAccessibilityStatus,
			backhaulAccessibilityStatus,
			selectedAssetSiteAvailabilityStatus,
			assetSiteAvailabilityStatus,
			selectedPowerSupplyAvailabilityStatus,
			powerSupplyAvailabilityStatus,
			selectedIsRentedBy,
			setSelectedPowerSupplyAvailabilityStatus,
			setSelectedAssetSiteAvailabilityStatus,
			setSelectedIsRentedBy,
			setSelectedType,
			setSelectedSubType,
			setSelectedContactPersonId,
			setSelectedAccessAvailability,
			setSelectedBackhaulAccessibilityStatus,
			assetTypeId,
			assetSubTypeId,
		});
	}, [
		assetSiteAvailabilityStatus,
		assetTypesSubtypes,
		assetSubTypesForFilter,
		assetSubTypeId,
		setSelectedSubType,
		selectedSubType,
		assetTypeId,
		backhaulAccessibilityStatus,
		contactPersonId,
		contactPersonsOptions,
		isAvailable,
		powerSupplyAvailabilityStatus,
		selectedAccessAvailability,
		selectedAssetSiteAvailabilityStatus,
		selectedBackhaulAccessibilityStatus,
		selectedContactPersonId,
		selectedPowerSupplyAvailabilityStatus,
		selectedType,
		selectedIsRentedBy,
		setSelectedIsRentedBy,
	]);

	const dateFilters: Array<ITableDatepickerFilter> = [
		{
			name: 'created',
			label: 'Created',
			handleChange: onChangeCreatedDate,
			selected: createdDate,
		},
		{
			name: 'updated',
			label: 'Updated',
			handleChange: onChangeUpdatedDate,
			selected: updatedDate,
		},
		{
			name: 'lastVerificationDate',
			label: 'Last verification date',
			handleChange: onChangeVerificationDate,
			selected: verificationDate,
		},
	];

	const handleExport = (assetsIds: Array<any>, actionId: TMenuActionsTypes) => {
		setSelectedAssetsIds(assetsIds);
		setActiveGroupTableAction(actionId);
		toggleFileTypeSelectionModal();
	};

	const handleChangeCommonValues = (ids: Array<any>, actionId: TMenuActionsTypes) => {
		setSelectedAssetsIds(ids);
		setActiveGroupTableAction(actionId);
		toggleSetCommonValues();
	};

	const isOpenFilters = !!(assetTypeId || contactPersonId || isAvailable || backhaulAccessibilityStatus
		|| assetSiteAvailabilityStatus || powerSupplyAvailabilityStatus || createdAt || updatedAt
		|| lastVerificationDate);

	return (
		<div className={styles.assetsListContainer}>
			<Modal
				isOpen={isShowDeleteConfirm}
				onClose={closeDeleteConfirm}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					isLoading={isDeletingAssets || isDeletingAsset}
					onClose={closeDeleteConfirm}
					onSubmit={deleteItemsHandler}
					question={`Are you sure you want to delete the asset${singleIdForRemove ? '' : 's'}?`}
				>
					{!singleIdForRemove && 'Assets that belongs to the active request(s) will be skipped.'}
				</Confirm>
			</Modal>
			<Modal
				isOpen={isAssetFormModal}
				isScrollableContent
				onClose={toggleCloseConfirmModal}
				title='Add asset'
				width={MODAL_WIDTH_580}
			>
				<AssetForm
					isLoadingForm={isCreatingAsset}
					setIsDirtyFormModal={setIsDirtyFormModal}
					submitHandler={handleCreateAsset}
				/>
			</Modal>
			<Modal
				isOpen={isCloseConfirmModal}
				onClose={toggleCloseConfirmModal}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onClose={toggleCloseConfirmModal}
					onSubmit={closeModals}
					{...modalCloseConfirm}
				/>
			</Modal>
			{isShowImportAsset
				&& <ImportMapDataContainer
					acceptedFileTypes={acceptedAssetFileTypes}
					importType={EImportingDataType.asset}
					isShowImportModal={isShowImportAsset}
					resultStatisticTitle='Skipped assets'
					toggleImportModal={toggleImportAsset}
				/>
			}
			{!!exportAssetsRules && (
				<Modal
					isOpen={isShowExportAssetRulesForm}
					onClose={toggleExportAssetRulesForm}
					title='Export rules'
					width={MODAL_WIDTH_580}
				>
					<ExportAssetsRulesForm
						exportAssetsRules={exportAssetsRules}
						isLoadingForm={isLoadingUpdateExportAssetRules}
						updateSubmitHandler={handleUpdateExportAssetsRules}
					/>
				</Modal>
			)}
			<Modal
				height='70vh'
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyCommonValues}
				isOpen={isOpenSetCommonValues}
				onClose={closeSetCommonValues}
				title='Set common values'
				width='750px'
			>
				<CommonValuesMapping
					activeGroupTableAction={activeGroupTableAction}
					closeSetCommonValues={closeSetCommonValues}
					filter={{
						assetSiteAvailabilityStatus,
						assetTypeId,
						contactPersonId,
						backhaulAccessibilityStatus,
						isAvailable,
						powerSupplyAvailabilityStatus,
						createdAt,
						updatedAt,
						lastVerificationDate,
					} as unknown as Record<string, string | number | boolean>}
					onClose={closeSetCommonValues}
					selectedAssetsIds={selectedAssetsIds}
					setIsDirty={setIsDirtyCommonValues}
				/>
			</Modal>
			<Modal
				isOpen={isOpenFileTypeSelectionModal}
				onClose={toggleFileTypeSelectionModal}
				title='File extension to export'
				width={MODAL_WIDTH_455}
			>
				<FileTypeSelectionForm
					activeGroupTableAction={activeGroupTableAction}
					closeModals={closeModals}
					exportRadioButtonsData={exportAssetsRadioButtonsData}
					filter={{
						assetSiteAvailabilityStatus,
						assetTypeId,
						contactPersonId,
						backhaulAccessibilityStatus,
						isAvailable,
						powerSupplyAvailabilityStatus,
						createdAt,
						updatedAt,
						lastVerificationDate,
					}}
					handlePreparingExport={handlePreparingExport}
					isLoading={isLoadingExportAssetsFile}
					selectedIds={selectedAssetsIds}
					toggleModal={toggleFileTypeSelectionModal}
				/>
			</Modal>
			{assetVisibilityData && (
				<Modal
					isOpen={!!(isShowAssetVisibilityConfirm && assetVisibilityData)}
					onClose={toggleAssetVisibilityModal}
					title='Confirm action'
					width={MODAL_WIDTH_480}
				>
					<Confirm
						onClose={toggleAssetVisibilityModal}
						onSubmit={handleChangeAssetsVisibility}
						question={getConfirmModalQuestionText(assetVisibilityData.isHide)}
					/>
				</Modal>
			)}
			<PageTitle
				icon={Icons.assetsActive()}
				title={EAdminPanelPageTitles.ManageAssets}
			/>
			<div className={styles.assetsListTableContainer}>
				<Table
					addTooltipText={addTooltipText}
					columns={columns}
					data={assetsList}
					dateFilters={dateFilters}
					filters={filters}
					handleChangeCommonValues={handleChangeCommonValues}
					handleCustomDelete={handleCustomDelete}
					handleExport={handleExport}
					handleExportRules={toggleExportAssetRulesForm}
					handleImport={toggleImportAsset}
					handleVisibilityAction={handleVisibilityAction}
					importTooltipText={importTooltipText}
					isCreatable
					isEditAccess={isEditAccess}
					isExport
					isExportSettings={assetOwnerOrganizationAdministrator}
					isImportable
					isLoading={isLoadingAssets}
					isOpenFilters={isOpenFilters}
					isOpenSearch={!!searchValue.length}
					isPinColumns
					isSetCommonValues={assetOwnerAttributeUpdater}
					isToggleAssetVisibility={isEditAccess}
					pagination={pagination}
					search={search}
					searchThreshold={0}
					setCheckedIds={setCheckedIds}
					sort={sort}
					stickyLeftColumnIndexes={isEditAccess ? [0, 1, 2, 3] : [0, 1, 2]}
					toggleAddModal={toggleAssetModal}
				/>
			</div>
		</div>
	);
};

export default AssetsList;
